import { useCallback, useEffect, useState } from "react";
import api, { formatErr } from "./api";

/** Wave V — shared stage-gate data hook.
 *
 * Lifts the stage-gate state out of StageWorkspaceTab so the rail
 * (always visible) and the active stage-detail pane don't fetch twice.
 *
 * File 47 (May 2026) — infrastructure & real-estate projects now use a
 * dynamic stage-gate map fetched from `/stage-gate-config`. When that
 * endpoint returns `use_legacy=false`, this hook transforms the
 * sub-sector-specific stages dict into the same shape the legacy rail
 * consumes (`{ stages: [...], catalogue: [...] }`). Status from the
 * legacy `/stages` endpoint is overlaid by code, so any stage that has
 * been run still shows its progress.
 */
export default function useStageGate(projectId) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [tick, setTick] = useState(0);

  const reload = useCallback(async () => {
    if (!projectId) return;
    try {
      // Fetch dynamic config first so we know whether to use File 47.
      let dynamic = null;
      try {
        const { data: dyn } = await api.get(
          `/projects/${projectId}/stage-gate-config`,
        );
        dynamic = dyn;
      } catch {
        // ignore — will fall back to legacy
      }

      const { data: legacy } = await api.get(
        `/projects/${projectId}/stages`,
      );

      if (dynamic && dynamic.use_legacy !== true
          && Object.keys(dynamic.stages || {}).length > 0) {
        const merged = _mergeFile47WithLegacy(dynamic, legacy);
        setData(merged);
      } else {
        setData(legacy);
      }
      setErr("");
    } catch (e) {
      const code = e.response?.status;
      if (code === 502 || code === 503 || code === 504) {
        if (!data) setErr("Backend is busy generating designs. Reloading…");
        return;
      }
      setErr(formatErr(e.response?.data?.detail) || e.message);
    }
  }, [projectId, data]);

  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [projectId, tick]);

  // Quietly retry until first successful load.
  useEffect(() => {
    if (data) return;
    const id = setInterval(() => setTick(t => t + 1), 5000);
    return () => clearInterval(id);
  }, [data]);

  // Auto-poll while any stage is in_progress.
  useEffect(() => {
    if (!data) return;
    const list = data.stages_flat || data.stages || [];
    const anyInProgress = list.some((s) => s.status === "in_progress");
    if (!anyInProgress) return;
    const id = setInterval(reload, 5000);
    return () => clearInterval(id);
  }, [data, reload]);

  const refresh = useCallback(() => setTick(t => t + 1), []);

  const toggleApprovalGates = useCallback(async () => {
    if (!data) return;
    try {
      await api.patch(`/projects/${projectId}/approval-gates`,
                              { enabled: !data.approval_gates_enabled });
      refresh();
    } catch (e) {
      setErr(formatErr(e.response?.data?.detail) || e.message);
    }
  }, [projectId, data, refresh]);

  return { data, err, reload, refresh, toggleApprovalGates };
}


/** File 47 → legacy shape adapter.
 *
 * Takes a File 47 stage-gate config:
 *   { domain, sub_type, display_name, stages: { A: {name, modules: [{code,name,required}]}, … } }
 * and emits the same shape useStageGate normally returns:
 *   { domain, sub_type, stages: [{code, name, status, …}], catalogue: [...], approval_gates_enabled }
 *
 * Status from the legacy /stages response is overlaid by code so any
 * actually-run stage still shows its progress.
 */
function _mergeFile47WithLegacy(dynamic, legacy) {
  // Flatten any nested legacy stages so we can match by code at any depth.
  const legacyByCode = {};
  const _walk = (arr) => (arr || []).forEach((s) => {
    if (s?.code) legacyByCode[s.code] = s;
    if (s?.sub_stages?.length) _walk(s.sub_stages);
  });
  _walk(legacy?.stages);

  const stagesNested = [];
  const stagesFlat = [];
  const catalogue = [];

  Object.entries(dynamic.stages || {}).forEach(([stageKey, stage]) => {
    const topLegacy = legacyByCode[stageKey] || {};

    // Build L2 children first so the parent can reference them.
    const subs = (stage.modules || []).map((m) => {
      const cleg = legacyByCode[m.code] || {};
      const child = {
        code: m.code,
        name: m.name,
        status: cleg.status || "not_started",
        required: m.required !== false,
        deliverables: cleg.deliverables || [],
        sub_stages: [],
        // Phase 5 Final WS4 — surface hard_deps from backend so the
        // rail can show a 🔒 + dependency tooltip when prereqs unmet.
        hard_deps: m.hard_deps || [],
        ...cleg,
      };
      stagesFlat.push(child);
      catalogue.push({
        code: m.code,
        name: m.name,
        required: m.required !== false,
        hard_deps: m.hard_deps || [],
      });
      return child;
    });

    const parent = {
      code: stageKey,
      name: stage.name || topLegacy.name || stageKey,
      status: topLegacy.status || "not_started",
      run_count: topLegacy.run_count || 0,
      deliverables: topLegacy.deliverables || [],
      ...topLegacy,
      sub_stages: subs,
    };
    stagesNested.push(parent);
    stagesFlat.push(parent);
    catalogue.push({ code: stageKey, name: stage.name || stageKey });
  });

  return {
    domain: dynamic.domain || legacy?.domain || "",
    sub_type: dynamic.sub_type,
    display_name: dynamic.display_name,
    approval_gates_enabled: legacy?.approval_gates_enabled ?? false,
    stages: stagesNested,            // hierarchical — parents with sub_stages
    stages_flat: stagesFlat,         // flat — for code lookup
    catalogue,
    use_dynamic: true,
    total_stages: dynamic.total_stages,
    total_modules: dynamic.total_modules,
  };
}
